/* eslint-disable react/no-array-index-key */
/* eslint-disable react/no-unknown-property */
import {
  Action,
  Card, FontWeight, HStack, Icon, ICON_CREATION_OUTLINE, ICON_FILE_DOCUMENT_OUTLINE, Message, ModalSize, Portal, Stack, Text, useModal,
} from '@doveit/bricks';
import React, { FunctionComponent } from 'react';
import { Document } from '../../../providers/api/dtos';
import { AIAnnotationCategoryType, DeedProvenanceAIAnnotationCategory } from '../../../domain/types';
import { aiAnnotationCategoryLabels, documentTypeLabels } from '../../../labels';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import * as styles from './AIDocumentAnalysisWidget.style';
import Divider from '../../../components/divider/Divider';

export const AI_ANALYSIS_DISCLAIMER_MESSAGE = "Analisi eseguita con l'intelligenza artificiale. Verifica sempre la correttezza delle informazioni.";

const isDeedProvenanceCategory = (category: string): category is DeedProvenanceAIAnnotationCategory => Object.values(DeedProvenanceAIAnnotationCategory).includes(category as DeedProvenanceAIAnnotationCategory);

export interface AIDocumentAnalysisWidgetProps {
  document: Document;
}

const AIDocumentAnalysisWidget: FunctionComponent<AIDocumentAnalysisWidgetProps> = ({
  document,
}) => {
  const modal = useModal();

  // We retrieve all the document files in the “document” folder and keep only those that have been analysed
  const analyzedFiles = React.useMemo(
    () => document.files.filter((file) => file.analysisResult?.annotations?.length),
    [document.files],
  );

  // Annotations for modal, grouped by file and category
  const annotationsByCategoryPerFile = React.useMemo(() => analyzedFiles.map((file) => {
    const grouped: Partial<Record<AIAnnotationCategoryType, string[]>> = {};
    file.analysisResult!.annotations
      // We keep only annotations with text, as those are the only ones we want to show in the preview and modal (the ones with structured data are not shown)
      .filter((annotation) => !!annotation.text)
      .forEach(({ category, text }) => {
        if (!grouped[category]) grouped[category] = [];
        grouped[category].push(text!);
      });

    return { file, annotationsByCategory: grouped };
  }), [analyzedFiles]);

  if (analyzedFiles.length === 0) {
    return null;
  }

  const sectionContent = (
    <Stack gap={150}>
      <Message
        type="warning"
        message={AI_ANALYSIS_DISCLAIMER_MESSAGE}
      />

      <Divider padded={false} />

      {annotationsByCategoryPerFile.map(({ file, annotationsByCategory: fileAnnotationsByCategory }) => (
        <div
          key={file.id ?? file.filename}
          css={styles.fileSection}
          data-ref="ai-document-analysis-widget-file-section"
        >
          <HStack>
            <Icon
              path={ICON_FILE_DOCUMENT_OUTLINE}
              size={16}
            />
            <Text.Body fontWeight={FontWeight.MEDIUM} color="neutral.default.highest">{documentTypeLabels[document.type]}</Text.Body>
            <Text.Body color="neutral.default.highest">({file.filename})</Text.Body>
          </HStack>

          {(Object.entries(fileAnnotationsByCategory)).map(([category, texts]) => (
            <Stack gap={150} key={category}>
              <Text.BodySmall fontWeight={FontWeight.MEDIUM} color="neutral.default.low">
                {isDeedProvenanceCategory(category) ? aiAnnotationCategoryLabels[category] : category}
              </Text.BodySmall>
              <ul>
                {texts?.map((text, idx) => (
                  <li key={idx} css={styles.annotationListItem}>
                    <Text.BodySmall>{text}</Text.BodySmall>
                  </li>
                ))}
              </ul>
            </Stack>
          ))}
        </div>
      ))}
    </Stack>
  );
  return (
    <Card
      aria-label="Sezione risultati analisi documento"
      data-ref="ai-document-analysis-widget"
    >
      <Card.Header
        title="Analisi documenti"
        icon={{ path: ICON_CREATION_OUTLINE }}
      />
      <Card.Content>
        <Stack gap={150}>
          <div css={styles.preview}>
            {sectionContent}
          </div>

          <div css={styles.actionWrapper}>
            <Action
              label="Mostra altro"
              size="XS"
              onClick={modal.open}
            />
          </div>
        </Stack>
      </Card.Content>
      <Portal>
        <SimpleModal
          title="Analisi documenti"
          isOpen={modal.isOpen}
          close={modal.close}
          size={ModalSize.MEDIUM}
          aria-label="Dettaglio risultati analisi documenti"
        >
          {sectionContent}
        </SimpleModal>
      </Portal>
    </Card>
  );
};

export default AIDocumentAnalysisWidget;
